import { create } from 'zustand';
import { PaginationType } from '../../../types';
import { StoreStatus } from '../../../static';
import { SavedFilter } from '../../query/saved-filters/useGetSavedFilters';

/**
 * Group Chats store
 */
interface useGroupChatsStore {
  teamInboxFilters: {
    searchText: string;
    groupId: string;
    // The whole object, not just the id: SavedFiltersDropdown runs in controlled mode
    // here, so it renders this directly and the label survives the collapsible filter
    // panel unmounting/remounting without waiting on the async filter-list fetch.
    savedFilter: SavedFilter | null;
  };
  isLoading: boolean;
  conversationList: any;
  selectedChat: any;
  isFetchedChatById: boolean;
  hasNewActivities: boolean;
  pagination: PaginationType;
  error: boolean;
  setState(nextState: Partial<useGroupChatsStore>): void;
  setSearchTextFilterValue(searchText: string): void;
  setGroupId(groupId: string): void;
  setSavedFilter(savedFilter: SavedFilter | null): void;
  resetTeamInboxPagination(): void;
  hasChat(chatId: string): boolean;
  getChatById(chatId: string): any | null;
  addChatToTop(chat: any, limit?: number | undefined): void;
  updateChatAsRead(chatId: string): void;
  updateReactionLastActivityAndMoveChatToTop(
    chatId: string,
    messageId: string,
    reaction: string,
    reactedBy: string,
    messageType: string,
    contentPreview: string,
    lastActivityAt: string
  ): void;
  updateChatLastActivitySilentAndIncrementUnreadMessageCount(
    chatId: string,
    lastActivity: any,
    lastActivityAt: string
  ): void;
  confirmOptimisticReaction(chatId: string, reaction: string, lastActivityAt: string): void;
  addOptimisticReactionWithBackup(
    chatId: string,
    reactionPayload: {
      messageId: string;
      reaction: string;
      reactedBy: string;
      messageType: string;
      contentPreview: string;
    },
    optimisticTime: string
  ): void;
  removeOptimisticReaction(chatId: string, reaction: string, reactedBy: string): void;
  restoreLastActivityAndResort(chatId: string, restoredLastActivity: any): void;
  updateChatLastActivity(chatId: string, lastActivity: any, lastActivityAt: string, incrementUnread: boolean): void;
}

const initialPagination = {
  limit: 50,
  page: 1,
  total: 0,
  pages: 0,
};

export const initialGroupChatsState = {
  teamInboxFilters: {
    searchText: '',
    groupId: '',
    savedFilter: null,
  },
  isLoading: true,
  conversationList: [],
  selectedChat: {},
  isFetchedChatById: true,
  hasNewActivities: false,
  pagination: initialPagination,
  error: false,
};

const createOptimisticBackup = (chat: any) => {
  if (chat.__optimisticBackup) return chat.__optimisticBackup;

  return {
    lastActivity: chat.lastActivity ?? null,
    lastActivityAt: chat.lastActivityAt ?? null,
  };
};

const useGroupChats = create<useGroupChatsStore>((set, getState) => ({
  ...initialGroupChatsState,
  setState: nextState => set(state => ({ ...state, ...nextState })),
  setSearchTextFilterValue: (searchText: string) =>
    set(state => ({
      teamInboxFilters: {
        ...state.teamInboxFilters,
        searchText,
      },
    })),
  setGroupId: (groupId: string) =>
    set(state => ({
      teamInboxFilters: {
        ...state.teamInboxFilters,
        groupId,
      },
    })),
  setSavedFilter: (savedFilter: SavedFilter | null) =>
    set(state => ({
      teamInboxFilters: {
        ...state.teamInboxFilters,
        savedFilter,
      },
    })),
  resetTeamInboxPagination: () =>
    set(() => ({
      pagination: initialPagination,
    })),
  hasChat: (chatId: string) => {
    return getState().conversationList.some((chat: any) => chat._id === chatId);
  },
  getChatById: (chatId: string) => {
    return getState().conversationList.find((chat: any) => chat._id === chatId) ?? null;
  },
  addChatToTop: (chat: any, limit = 50) =>
    set(state => {
      const exists = state.conversationList.some((c: any) => c._id === chat._id);

      const withoutCurrent = state.conversationList.filter((c: any) => c._id !== chat._id);

      const nextList = [
        exists
          ? {
              ...state.conversationList.find((c: any) => c._id === chat._id),
              ...chat,
            }
          : chat,
        ...withoutCurrent,
      ];

      return {
        conversationList: nextList.slice(0, limit),
      };
    }),
  updateChatAsRead: (chatId: string) =>
    set(state => ({
      conversationList: state.conversationList.map((chat: any) =>
        chat._id === chatId
          ? {
              ...chat,
              unreadMessageCount: 0,
            }
          : chat
      ),
    })),
  updateReactionLastActivityAndMoveChatToTop: (
    chatId: string,
    messageId: string,
    reaction: string,
    reactedBy: string,
    messageType: string,
    contentPreview: string,
    lastActivityAt: string
  ) =>
    set(state => {
      const existing = state.conversationList.find((c: any) => c._id === chatId);

      if (!existing) return state;

      const updatedChat = {
        ...existing,
        lastActivity: {
          type: 'reaction',
          reaction: {
            messageId: messageId,
            reaction: reaction,
            reactedBy: reactedBy,
            messageType: messageType,
            contentPreview: contentPreview,
          },
        },
        lastActivityAt: lastActivityAt,
      };

      return {
        conversationList: [updatedChat, ...state.conversationList.filter((c: any) => c._id !== chatId)],
      };
    }),
  updateChatLastActivitySilentAndIncrementUnreadMessageCount: (
    chatId: string,
    lastActivity: any,
    lastActivityAt: string
  ) =>
    set(state => {
      const existingChat = state.conversationList.find((chat: any) => chat._id === chatId);

      if (!existingChat) {
        return state;
      }

      const updatedChat = {
        ...existingChat,
        lastActivity,
        lastActivityAt,
        unreadMessageCount: (existingChat.unreadMessageCount ?? 0) + 1,
      };

      return {
        conversationList: [updatedChat, ...state.conversationList.filter((chat: any) => chat._id !== chatId)],
      };
    }),
  confirmOptimisticReaction: (chatId: string, reaction: string, lastActivityAt: string) =>
    set(state => ({
      conversationList: state.conversationList.map((chat: any) =>
        chat._id !== chatId
          ? chat
          : {
              ...chat,
              lastActivity:
                chat.lastActivity?.type === 'reaction'
                  ? {
                      ...chat.lastActivity,
                      reaction: {
                        ...chat.lastActivity.reaction,
                        reaction,
                      },
                    }
                  : chat.lastActivity,
              lastActivityAt,
            }
      ),
    })),
  addOptimisticReactionWithBackup: (
    chatId: string,
    reactionPayload: {
      messageId: string;
      reaction: string;
      reactedBy: string;
      messageType: string;
      contentPreview: string;
    },
    createdAt: string
  ) =>
    set(state => {
      const chat = state.conversationList.find((c: any) => c._id === chatId);
      if (!chat) return state;

      const updatedChat = { ...chat };

      const lastActivity = chat.lastActivity;

      const lastIsSameReaction =
        lastActivity?.type === 'reaction' &&
        lastActivity.reaction?.reaction === reactionPayload.reaction &&
        lastActivity.reaction?.reactedBy === reactionPayload.reactedBy;

      if (!lastIsSameReaction && !chat.__optimisticBackup) {
        updatedChat.__optimisticBackup = {
          lastActivity: lastActivity ?? null,
          lastActivityAt: chat.lastActivityAt ?? null,
        };
      }

      updatedChat.lastActivity = {
        type: 'reaction',
        reaction: { ...reactionPayload, status: StoreStatus.PENDING },
      };
      updatedChat.lastActivityAt = createdAt;

      return {
        conversationList: [updatedChat, ...state.conversationList.filter((c: any) => c._id !== chatId)],
      };
    }),
  restoreLastActivityAndResort: (chatId: string, restoredLastActivity: any) =>
    set(state => ({
      conversationList: state.conversationList.map((chat: any) =>
        chat._id !== chatId
          ? chat
          : {
              ...chat,
              lastActivity: restoredLastActivity,
            }
      ),
    })),
  removeOptimisticReaction: (chatId: string, reaction: string, reactedBy: string) =>
    set(state => ({
      conversationList: state.conversationList.map((chat: any) => {
        if (chat._id !== chatId) return chat;

        const lastReaction = chat.lastActivity?.type === 'reaction' ? chat.lastActivity.reaction : null;

        if (lastReaction && lastReaction.reaction === reaction && lastReaction.reactedBy === reactedBy) {
          return {
            ...chat,
            lastActivity: chat.__optimisticBackup?.lastActivity ?? chat.lastActivity,
            lastActivityAt: chat.__optimisticBackup?.lastActivityAt ?? chat.lastActivityAt,
            __optimisticBackup: undefined,
          };
        }

        return chat;
      }),
    })),
  updateChatLastActivity: (chatId: string, lastActivity: any, lastActivityAt: string, incrementUnread: boolean) =>
    set(state => {
      const chat = state.conversationList.find((c: any) => c._id === chatId);
      if (!chat) return state;

      const updatedChat = {
        ...chat,
        lastActivity,
        lastActivityAt,
        unreadMessageCount: incrementUnread ? (chat.unreadMessageCount ?? 0) + 1 : (chat.unreadMessageCount ?? 0),
      };

      return {
        conversationList: [updatedChat, ...state.conversationList.filter((c: any) => c._id !== chatId)],
      };
    }),
}));

export default useGroupChats;
